import React, { memo } from 'react';

import LinkComp from 'components/LinkComp/LinkComp';
import Text from 'components/Text/Text';
import { useCopyStore } from 'store';
import { ReactComponent as SvgInstagramIcon } from 'svgs/instagramIcon.svg';
import { ReactComponent as SvgLinkedInIcon } from 'svgs/linkedInIcon.svg';
import { MediaCMS } from 'utils/sharedStrapiQueries/sharedTypes';
import { colors } from 'utils/styles/theme';

import { formatDate } from './SocialMediaTile.utils';

import * as S from './SocialMediaTile.styles';

export interface SocialMediaTileProps {
  id: string;
  image: MediaCMS;
  handle: string;
  socialMediaType: 'linkedIn' | 'instagram';
  date: string;
  headline: string;
  subCopy: string;
  postUrl: string;
}

const SocialMediaTile: React.FunctionComponent<SocialMediaTileProps> = ({
  image,
  handle,
  socialMediaType,
  date,
  headline,
  subCopy,
  postUrl,
}) => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);

  const hoverTextColor =
    globalSettings?.buttonsHoverColor ||
    globalSettings?.buttonsHoverDefaultColors;

  const typeOfSocialMedia = {
    instagram: {
      url: `https://www.instagram.com/${handle}`,
      icon: <SvgInstagramIcon />,
    },
    linkedIn: {
      url: `https://www.linkedin.com/company/${handle}`,
      icon: <SvgLinkedInIcon />,
    },
  };

  const link = (href, text) => (
    <LinkComp href={href} isExternal isCursorHoverState={false} onlyLink>
      {text}
    </LinkComp>
  );

  return (
    <S.SocialMediaTileWrapper hoverTextColor={hoverTextColor}>
      <S.ImageWrapper>
        {image?.url && (
          <S.Image src={image.url} alt={image?.alternativeText || ''} />
        )}
      </S.ImageWrapper>

      <S.PostInfoWrapper>
        <S.LeftSideSocialWrapper>
          {typeOfSocialMedia[socialMediaType]?.icon ??
            typeOfSocialMedia['instagram'].icon}
          <Text
            as="small"
            size="small"
            text={'@' + handle}
            color={colors.brightGray}
          >
            {link(
              typeOfSocialMedia[socialMediaType]?.url ??
                typeOfSocialMedia['instagram'].url,
              '@' + handle
            )}
          </Text>
        </S.LeftSideSocialWrapper>
        <Text
          as="small"
          size="small"
          text={formatDate(date)}
          color={colors.gray}
        />
      </S.PostInfoWrapper>

      <Text as="h5" size="heading5" text={headline}>
        {postUrl && link(postUrl, headline)}
      </Text>
      <Text size="smallBody" text={subCopy} color={colors.tundora}>
        {postUrl && link(postUrl, subCopy)}
      </Text>
    </S.SocialMediaTileWrapper>
  );
};

export default memo(SocialMediaTile);
